import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "./SidebarLayout";
import PayoutWeekdayCard from "../../components/PayoutWeekdayCard";

/**
 * OwnerPayoutHistory — Phase 5 Frontend Transparency for R1
 *
 * Displays:
 *   - Summary cards: Held, Released (pending transfer), Paid This Month
 *   - Collapsible "Held" section: entries still in the business hold window
 *   - Collapsible "Released" section: entries awaiting next batch transfer
 *   - Collapsible "History" section: past paid-out batches
 *   - CSV export
 *
 * Data source priority:
 *   1. GET /api/payout-ledger/business/reconciliation
 */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const OwnerPayoutHistory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    held: true,
    released: true,
    history: false,
  });
  const [instantPayoutData, setInstantPayoutData] = useState(null);
  const [loadingInstantPayout, setLoadingInstantPayout] = useState(false);
  const [processingInstantPayout, setProcessingInstantPayout] = useState(false);
  const [instantPayoutError, setInstantPayoutError] = useState("");
  const [instantPayoutSuccess, setInstantPayoutSuccess] = useState("");
  const [instantPayoutVisible, setInstantPayoutVisible] = useState(false);

  // -------------------------------------------------------------------------
  // Auth helpers
  // -------------------------------------------------------------------------
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // -------------------------------------------------------------------------
  // Fetch
  // -------------------------------------------------------------------------
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      const res = await fetch(
        `${API_BASE_URL}/api/payout-ledger/business/reconciliation`,
        { headers },
      );

      if (res.status === 401) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
        return;
      }
      if (!res.ok) throw new Error("Failed to fetch payout reconciliation");
      const json = await res.json();
      setData(json);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchInstantPayoutAvailability = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    setLoadingInstantPayout(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/payout-ledger/business/instant-payout/available`,
        { headers },
      );

      if (res.status === 401) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
        return;
      }

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.message || "Failed to load instant payout data");
      }

      if (json.instantPayoutEnabled === false || json.isEnabled === false) {
        setInstantPayoutData(null);
        setInstantPayoutVisible(false);
        setInstantPayoutError("");
        return;
      }

      setInstantPayoutData(json);
      setInstantPayoutVisible(true);
      setInstantPayoutError("");
    } catch (err) {
      setInstantPayoutData(null);
      setInstantPayoutVisible(false);
      setInstantPayoutError(err.message || "Failed to load instant payout data");
    } finally {
      setLoadingInstantPayout(false);
    }
  }, [getAuthHeaders, navigate]);

  useEffect(() => {
    fetchInstantPayoutAvailability();
  }, [fetchInstantPayoutAvailability]);

  const handleRequestInstantPayout = async () => {
    if (!instantPayoutData?.canRequestPayout) return;

    const confirmed = window.confirm(
      `You will receive $${instantPayoutData.netAmount.toFixed(2)} after a ${instantPayoutData.instantFeePercent}% fee ($${instantPayoutData.instantFee.toFixed(2)}). Proceed with instant payout?`,
    );
    if (!confirmed) return;

    const headers = getAuthHeaders();
    if (!headers) return;

    setProcessingInstantPayout(true);
    setInstantPayoutError("");
    setInstantPayoutSuccess("");

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/payout-ledger/business/instant-payout/request`,
        {
          method: "POST",
          headers,
        },
      );

      if (res.status === 401) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
        return;
      }

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.message || "Failed to process instant payout");
      }

      setInstantPayoutSuccess(
        `Instant payout of $${Number(json.netAmount || 0).toFixed(2)} processed successfully.`,
      );
      await fetchData();
      await fetchInstantPayoutAvailability();
    } catch (err) {
      setInstantPayoutError(err.message || "Failed to process instant payout");
    } finally {
      setProcessingInstantPayout(false);
    }
  };

  // -------------------------------------------------------------------------
  // CSV export
  // -------------------------------------------------------------------------
  const exportToCSV = () => {
    if (!data) return;

    const rows = [
      ["Type", "Source", "Gross", "Net Eligible", "Status", "Date"].join(","),
    ];

    const addEntries = (list, status) => {
      list.forEach((e) => {
        rows.push(
          [
            e.sourceType || "business",
            e.bookingId || e.boothRentalPaymentId || e._id,
            `$${(e.grossAmount || 0).toFixed(2)}`,
            `$${(e.netEligibleAmount || 0).toFixed(2)}`,
            status,
            e.createdAt ? new Date(e.createdAt).toLocaleDateString() : "",
          ].join(","),
        );
      });
    };

    addEntries(data.held?.entries || [], "held");
    addEntries(data.released?.entries || [], "released");

    // History rows
    (data.history || []).forEach((h) => {
      rows.push(
        [
          "payout_batch",
          h.entryCount + " entries",
          "",
          `$${(h.amount || 0).toFixed(2)}`,
          "paid_out",
          h.payoutDate,
        ].join(","),
      );
    });

    const csv = rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `business_payouts_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------
  const toggleSection = (key) =>
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const formatCountdown = (releaseDate) => {
    if (!releaseDate) return "—";
    const diff = new Date(releaseDate) - new Date();
    if (diff <= 0) return "Releasing soon...";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    return `${hours}h ${minutes}m`;
  };

  const sourceLabel = (sourceType) => {
    const map = {
      booking_service: "Booking — Service",
      booking_product: "Booking — Product",
      order_product: "Order — Product",
      booth_rent: "Booth Rent",
      // The two package rails. A ledger entry with no label prints its RAW KEY to the owner, so
      // "package_shop_share" would appear on the page as though the screen had broken.
      package_shop_share: "Package — Shop share",
      package_session: "Package — Session",
      manual: "Manual",
    };
    return map[sourceType] || sourceType || "—";
  };

  const fmt = (n) => `$${(n || 0).toFixed(2)}`;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <SidebarLayout>
        <div style={{ padding: "20px" }}>
          <h2>💼 Business Payouts & Reconciliation</h2>
          
          {/* Payout Weekday Card - Always visible */}
          <PayoutWeekdayCard beneficiaryType="business" compact={true} />
          
          <p>Loading payout data...</p>
        </div>
      </SidebarLayout>
    );
  }

  if (!data) {
    return (
      <SidebarLayout>
        <div style={{ padding: "20px" }}>
          <h2>💼 Business Payouts & Reconciliation</h2>
          
          {/* Payout Weekday Card - Always visible */}
          <PayoutWeekdayCard beneficiaryType="business" compact={true} />
          
          <p style={{ color: "#ef4444" }}>
            Unable to load payout data. Please try again.
          </p>
        </div>
      </SidebarLayout>
    );
  }

  const heldEntries = data.held?.entries || [];
  const releasedEntries = data.released?.entries || [];
  const now = new Date();
  const futureHeldEntries = heldEntries.filter((entry) => {
    const releaseAt = entry?.autoReleaseAt || entry?.heldUntil;
    if (!releaseAt) return false;
    const releaseDate = new Date(releaseAt);
    return !Number.isNaN(releaseDate.getTime()) && releaseDate > now;
  });
  const heldTotal = data.held?.total || 0;
  const releasedTotal = data.released?.total || 0;
  const futureHeldTotal = futureHeldEntries.reduce(
    (sum, entry) => sum + (entry.netEligibleAmount || entry.grossAmount || 0),
    0,
  );
  const history = data.history || [];
  const paidThisMonth = data.totalPaidThisMonth || 0;

  const sectionCard = (style, children) => (
    <div
      style={{
        background: "white",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        marginBottom: "16px",
        ...style,
      }}
    >
      {children}
    </div>
  );

  const sectionHeader = (key, icon, label, subtitle, borderColor) => (
    <div
      onClick={() => toggleSection(key)}
      style={{
        padding: "16px 20px",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: expandedSections[key]
          ? `1px solid ${borderColor}`
          : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <span style={{ fontSize: "20px" }}>{icon}</span>
        <div>
          <div style={{ fontWeight: "600", fontSize: "16px" }}>{label}</div>
          <div style={{ fontSize: "13px", color: "#6b7280" }}>{subtitle}</div>
        </div>
      </div>
      <span style={{ fontSize: "18px", color: "#6b7280" }}>
        {expandedSections[key] ? "▼" : "▶"}
      </span>
    </div>
  );

  return (
    <SidebarLayout>
      <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
        {/* ---- Header ---- */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>💼 Business Payouts & Reconciliation</h2>
            <span
              style={{
                display: "inline-block",
                marginTop: "6px",
                padding: "2px 10px",
                background: "#dcfce7",
                color: "#166534",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              ✓ Live Ledger
            </span>
          </div>
          <button
            onClick={exportToCSV}
            style={{
              padding: "10px 20px",
              background: "#10b981",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            📥 Export CSV
          </button>
        </div>

        {/* Payout Weekday Card */}
        <PayoutWeekdayCard beneficiaryType="business" compact={true} />

        {/* ---- Summary Cards ---- */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "16px",
            marginBottom: "32px",
          }}
        >
          <div
            style={{
              padding: "20px",
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              borderRadius: "12px",
              color: "white",
              boxShadow: "0 4px 6px rgba(245, 158, 11, 0.3)",
            }}
          >
            <div
              style={{ fontSize: "13px", opacity: 0.9, marginBottom: "8px" }}
            >
              🔒 HELD (In Hold Window)
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", lineHeight: 1 }}
            >
              {fmt(heldTotal)}
            </div>
            <div style={{ fontSize: "12px", marginTop: "8px", opacity: 0.9 }}>
              {heldEntries.length} holding
            </div>
          </div>

          <div
            style={{
              padding: "20px",
              background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
              borderRadius: "12px",
              color: "white",
              boxShadow: "0 4px 6px rgba(59, 130, 246, 0.3)",
            }}
          >
            <div
              style={{ fontSize: "13px", opacity: 0.9, marginBottom: "8px" }}
            >
              📤 RELEASED (Queued for Transfer)
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", lineHeight: 1 }}
            >
              {fmt(releasedTotal)}
            </div>
            <div style={{ fontSize: "12px", marginTop: "8px", opacity: 0.9 }}>
              {data.nextPayoutDate
                ? `Next: ${new Date(data.nextPayoutDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`
                : "Awaiting next batch"}
            </div>
          </div>

          <div
            style={{
              padding: "20px",
              background: "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)",
              borderRadius: "12px",
              color: "white",
              boxShadow: "0 4px 6px rgba(99, 102, 241, 0.3)",
            }}
          >
            <div
              style={{ fontSize: "13px", opacity: 0.9, marginBottom: "8px" }}
            >
              ⏳ FUTURE PAYMENTS
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", lineHeight: 1 }}
            >
              {fmt(futureHeldTotal)}
            </div>
            <div style={{ fontSize: "12px", marginTop: "8px", opacity: 0.9 }}>
              {futureHeldEntries.length} locked until release date
            </div>
          </div>

          <div
            style={{
              padding: "20px",
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              borderRadius: "12px",
              color: "white",
              boxShadow: "0 4px 6px rgba(16, 185, 129, 0.3)",
            }}
          >
            <div
              style={{ fontSize: "13px", opacity: 0.9, marginBottom: "8px" }}
            >
              ✅ PAID THIS MONTH
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", lineHeight: 1 }}
            >
              {fmt(paidThisMonth)}
            </div>
            <div style={{ fontSize: "12px", marginTop: "8px", opacity: 0.9 }}>
              {
                history.filter((h) => {
                  const d = new Date(h.payoutDate);
                  const now = new Date();
                  return (
                    d.getMonth() === now.getMonth() &&
                    d.getFullYear() === now.getFullYear()
                  );
                }).length
              }{" "}
              payout(s)
            </div>
          </div>
        </div>

        {instantPayoutVisible && (
          <div
            style={{
              background: "white",
              padding: "20px",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              marginBottom: "24px",
              border: "1px solid #dbeafe",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
                marginBottom: "12px",
              }}
            >
              <div>
                <div style={{ fontSize: "18px", fontWeight: "600", marginBottom: "4px" }}>
                  ⚡ Instant Payout
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  Pull currently released business earnings forward instead of waiting for the next scheduled transfer.
                </div>
              </div>
              <button
                onClick={handleRequestInstantPayout}
                disabled={
                  loadingInstantPayout ||
                  processingInstantPayout ||
                  !instantPayoutData?.canRequestPayout
                }
                style={{
                  padding: "12px 18px",
                  background:
                    instantPayoutData?.canRequestPayout && !processingInstantPayout
                      ? "#0f766e"
                      : "#9ca3af",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor:
                    instantPayoutData?.canRequestPayout && !processingInstantPayout
                      ? "pointer"
                      : "not-allowed",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {processingInstantPayout
                  ? "Processing..."
                  : "Request Instant Payout"}
              </button>
            </div>

            {loadingInstantPayout ? (
              <p style={{ margin: 0, color: "#6b7280", fontSize: "14px" }}>
                Checking instant payout availability...
              </p>
            ) : (
              <>
                {instantPayoutData && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                      gap: "12px",
                      marginBottom: "12px",
                    }}
                  >
                    <div style={{ padding: "12px", background: "#eff6ff", borderRadius: "8px" }}>
                      <div style={{ fontSize: "12px", color: "#1d4ed8", marginBottom: "4px" }}>
                        Available Now
                      </div>
                      <div style={{ fontSize: "22px", fontWeight: "700", color: "#1e3a8a" }}>
                        ${Number(instantPayoutData.grossAmount || 0).toFixed(2)}
                      </div>
                    </div>
                    <div style={{ padding: "12px", background: "#fff7ed", borderRadius: "8px" }}>
                      <div style={{ fontSize: "12px", color: "#c2410c", marginBottom: "4px" }}>
                        Instant Fee
                      </div>
                      <div style={{ fontSize: "22px", fontWeight: "700", color: "#9a3412" }}>
                        ${Number(instantPayoutData.instantFee || 0).toFixed(2)}
                      </div>
                    </div>
                    <div style={{ padding: "12px", background: "#ecfdf5", borderRadius: "8px" }}>
                      <div style={{ fontSize: "12px", color: "#047857", marginBottom: "4px" }}>
                        Net Deposit
                      </div>
                      <div style={{ fontSize: "22px", fontWeight: "700", color: "#065f46" }}>
                        ${Number(instantPayoutData.netAmount || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                )}

                {instantPayoutData?.processingCount > 0 && (
                  <div
                    style={{
                      padding: "10px 12px",
                      background: "#f8fafc",
                      border: "1px solid #cbd5e1",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#475569",
                      marginBottom: "12px",
                    }}
                  >
                    ${Number(instantPayoutData.processingAmount || 0).toFixed(2)} across {instantPayoutData.processingCount} released entr{instantPayoutData.processingCount !== 1 ? "ies are" : "y is"} already processing for the next scheduled payout and cannot be pulled into an instant payout.
                  </div>
                )}

                {instantPayoutData?.reason && (
                  <div
                    style={{
                      padding: "10px 12px",
                      background: "#f8fafc",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#475569",
                    }}
                  >
                    {instantPayoutData.reason}
                  </div>
                )}

                {instantPayoutData?.nextEligiblePayoutDate && !instantPayoutData?.canRequestPayout && (
                  <div
                    style={{
                      padding: "10px 12px",
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#1d4ed8",
                      marginTop: "12px",
                    }}
                  >
                    Next eligible payout window: {instantPayoutData.nextEligiblePayoutDate}
                  </div>
                )}
              </>
            )}

            {instantPayoutError && (
              <div
                style={{
                  padding: "10px 12px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: "#b91c1c",
                  marginTop: "12px",
                }}
              >
                {instantPayoutError}
              </div>
            )}

            {instantPayoutSuccess && (
              <div
                style={{
                  padding: "10px 12px",
                  background: "#ecfdf5",
                  border: "1px solid #a7f3d0",
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: "#065f46",
                  marginTop: "12px",
                }}
              >
                {instantPayoutSuccess}
              </div>
            )}
          </div>
        )}

        {/* ---- Held Section ---- */}
        {sectionCard(
          {},
          <>
            {sectionHeader(
              "held",
              "🔒",
              "Held (In Hold Window)",
              `${heldEntries.length} entr${heldEntries.length !== 1 ? "ies" : "y"} · ${fmt(heldTotal)}`,
              "#e5e7eb",
            )}
            {expandedSections.held && (
              <div style={{ padding: "20px" }}>
                {heldEntries.length === 0 ? (
                  <p style={{ color: "#9ca3af", textAlign: "center" }}>
                    No earnings currently in hold period
                  </p>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {heldEntries.map((entry) => (
                      <div
                        key={entry._id}
                        style={{
                          padding: "16px",
                          background: "#fef3c7",
                          border: "2px solid #f59e0b",
                          borderRadius: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontWeight: "600",
                                fontSize: "15px",
                                marginBottom: "4px",
                              }}
                            >
                              {sourceLabel(entry.sourceType)}
                            </div>
                            {entry.autoReleaseAt && (
                              <div
                                style={{
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  color: "#92400e",
                                }}
                              >
                                ⏱️ Releases in{" "}
                                {formatCountdown(entry.autoReleaseAt)}
                              </div>
                            )}
                            <div style={{ fontSize: "12px", color: "#78350f" }}>
                              Created:{" "}
                              {entry.createdAt
                                ? new Date(entry.createdAt).toLocaleDateString()
                                : "—"}
                            </div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div
                              style={{
                                fontWeight: "bold",
                                fontSize: "18px",
                                color: "#92400e",
                              }}
                            >
                              {fmt(entry.netEligibleAmount)}
                            </div>
                            <div style={{ fontSize: "11px", color: "#78350f" }}>
                              Gross {fmt(entry.grossAmount)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>,
        )}

        {/* ---- Released Section ---- */}
        {sectionCard(
          {},
          <>
            {sectionHeader(
              "released",
              "📤",
              "Released (Queued for Transfer)",
              `${releasedEntries.length} entr${releasedEntries.length !== 1 ? "ies" : "y"} · ${fmt(releasedTotal)}`,
              "#e5e7eb",
            )}
            {expandedSections.released && (
              <div style={{ padding: "20px" }}>
                {releasedEntries.length === 0 ? (
                  <p style={{ color: "#9ca3af", textAlign: "center" }}>
                    No earnings currently released and awaiting transfer
                  </p>
                ) : (
                  <>
                    {data.nextPayoutDate && (
                      <div
                        style={{
                          padding: "12px",
                          background: "#dbeafe",
                          borderRadius: "8px",
                          marginBottom: "16px",
                          fontSize: "14px",
                          color: "#1e40af",
                        }}
                      >
                          💡 These earnings will be included in your next weekly payout on{" "}
                          <strong>
                            {`${new Date(data.nextPayoutDate).toLocaleDateString("en-US", {
                              month: "2-digit",
                              day: "2-digit",
                              year: "numeric",
                            })} at 2:00 AM CT`}
                          </strong>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      {releasedEntries.map((entry) => (
                        <div
                          key={entry._id}
                          style={{
                            padding: "16px",
                            background: "#f0f9ff",
                            border: "2px solid #3b82f6",
                            borderRadius: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontWeight: "600",
                                  fontSize: "15px",
                                  marginBottom: "4px",
                                }}
                              >
                                ✅ {sourceLabel(entry.sourceType)}
                              </div>
                              <div
                                style={{ fontSize: "12px", color: "#6b7280" }}
                              >
                                Released:{" "}
                                {entry.payoutReleasedAt
                                  ? new Date(
                                      entry.payoutReleasedAt,
                                    ).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
                                  : "—"}
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div
                                style={{
                                  fontWeight: "bold",
                                  fontSize: "18px",
                                  color: "#1e40af",
                                }}
                              >
                                {fmt(entry.netEligibleAmount)}
                              </div>
                              <div
                                style={{ fontSize: "11px", color: "#6b7280" }}
                              >
                                Gross {fmt(entry.grossAmount)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </>,
        )}

        {/* ---- History Section ---- */}
        {sectionCard(
          {},
          <>
            {sectionHeader(
              "history",
              "📜",
              "Payout History",
              `${history.length} payout${history.length !== 1 ? "s" : ""}`,
              "#e5e7eb",
            )}
            {expandedSections.history && (
              <div style={{ padding: "20px" }}>
                {history.length === 0 ? (
                  <p style={{ color: "#9ca3af", textAlign: "center" }}>
                    No payout history yet
                  </p>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {history.map((h, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: "16px",
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontWeight: "600",
                              fontSize: "15px",
                              marginBottom: "4px",
                            }}
                          >
                            {new Date(h.payoutDate).toLocaleDateString(
                              "en-US",
                              {
                                weekday: "short",
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}
                          </div>
                          <div style={{ fontSize: "13px", color: "#6b7280" }}>
                            {h.entryCount} earning
                            {h.entryCount !== 1 ? "s" : ""} settled
                          </div>
                        </div>
                        <div
                          style={{
                            fontWeight: "bold",
                            fontSize: "20px",
                            color: "#059669",
                          }}
                        >
                          {fmt(h.amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>,
        )}
      </div>
    </SidebarLayout>
  );
};

export default OwnerPayoutHistory;
